// Import react elements
import React, { useEffect, useRef, useState } from 'react';

// Import createPortal from react-dom (native React)
import { createPortal } from 'react-dom';

// Import utilities
import { S2B } from "global/utilities/BooleanConverters";

// Import CSS for this component
import './ContextMenuButton.css';



// Create a custom context menu to appear near/next to a button which is clicked (used in orders table, pod products table etc.)
const ContextMenuButton = ({

    // Title of the context menu button
    buttonTitle,

    // Dropwodwn options of functions to run on the selected ID or IDs
    menuOptions,

    // The ID/s to run the function on
    selectedIDs,

    // The details of the table row to run the function on
    selectedDetails,

    disabled,

    // Pass in true/flase boolean if the item is active or not - will be null if item doesn't have active value
    itemIsActive,

    // Pass in a single function if you want the button to execute that function only onClick
    singleOnClickFunction,

}) => {



    // Track menu visibility
    const [menuVisible, setMenuVisible] = useState(false);

    // Track menu position
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

    // Track if menu should open above
    const [openAbove, setOpenAbove] = useState(false);

    // Reference for the trigger button
    const buttonRef = useRef(null);

    // Reference for the menu element
    const menuRef = useRef(null);



    // Handle button click to open the menu
    const handleButtonClick = (event) => {

        // Prevent row click bubbling
        event.stopPropagation();

        // If the menu is already open, close it
        if (menuVisible) {

            // Close menu
            setMenuVisible(false);

            // Exit early to avoid recalculating position
            return; 
        }


        // Get button position
        const buttonRect = buttonRef.current.getBoundingClientRect();

        // Set initial position (below the button)
        setMenuPosition({
            top: buttonRect.bottom + window.scrollY,
            left: buttonRect.left + window.scrollX
        });

        // Show the menu
        setMenuVisible(true);
    };



    // Handle clicks outside to close the menu
    useEffect(() => {

        const handleClickOutside = (event) => {

            if (menuVisible && !buttonRef.current.contains(event.target) && !menuRef.current.contains(event.target)) {

                setMenuVisible(false);

            }

        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {

            document.removeEventListener('mousedown', handleClickOutside);

        };

    }, [menuVisible]);



    // Recalculate menu position if menu is visible - so it appears on top if there isn't enough space below
    useEffect(() => {

        if (menuVisible && menuRef.current) {

            const buttonRect = buttonRef.current.getBoundingClientRect();
            const menuRect = menuRef.current.getBoundingClientRect();

            const spaceBelow = window.innerHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            if (spaceBelow < menuRect.height && spaceAbove > spaceBelow) {

                // Open above
                setMenuPosition({
                    top: buttonRect.top + window.scrollY - menuRect.height,
                    left: buttonRect.left + window.scrollX
                });

                setOpenAbove(true);

            } else {

                // Open below
                setMenuPosition({
                    top: buttonRect.bottom + window.scrollY,
                    left: buttonRect.left + window.scrollX
                });

                setOpenAbove(false);
            }
        }

    }, [menuVisible]);



    return (

        <div className="context-menu-button">

            {/* Trigger Button */}
            <button
                ref={buttonRef}
                className="context-menu-button__trigger"
                onClick={(event) => {

                    // Check if there is a single function passed in
                    if (singleOnClickFunction) {

                        // If so run it
                        singleOnClickFunction();

                    } else {

                        // Else open the context menu
                        handleButtonClick(event);

                    }
                }}
                disabled={disabled}
            >

                {buttonTitle}

            </button>

            {/* Render the menu using createPortal */}
            {menuVisible && createPortal(

                <div
                    ref={menuRef}
                    className={`context-menu-button__menu ${openAbove ? 'context-menu-button__menu--above' : 'context-menu-button__menu--below'}`}
                    style={{
                        position: 'absolute',
                        top: `${menuPosition.top}px`,
                        left: `${menuPosition.left}px`
                    }}
                >

                    {/* Render each menu option */}
                    {menuOptions.map((option, index) => (

                        <button
                            key={index}
                            className="context-menu-button__option"
                            onClick={(e) => {

                                // Prevent table row click 
                                e.stopPropagation();

                                // Run the clickFunction assigned in the actions config
                                option.clickFunction([selectedIDs], option);

                                // Close the dropdown menu
                                setMenuVisible(false);

                            }}
                            disabled={
                                typeof option.disabled === "function"
                                    ? option.disabled(selectedDetails)
                                    : option.disabled
                            }
                            style={{ color: option.color }}
                        >

                            { /* option.icon */ }

                            { // Check if an activeLabel exists (so we can show different labels for active/inactive items that are to be actioned (pod products show/hide on store from table))
                                option.activeLabel ?

                                    // It does exist so check if the item is active
                                    S2B(itemIsActive) ?

                                        // It is active so show the activeLabel
                                        option.activeLabel

                                        // Otheriwse it's not active so show the inactiveLabel
                                        : !S2B(itemIsActive) ? option.inactiveLabel

                                            // Default to the label because this menu option doesn't care about the item's active state
                                            : option.label

                                    // The activeLabel doesn't exist so just show the default label
                                    : option.label

                            }

                        </button>

                    ))}

                </div>,

                // Render outside the table in the DOM
                document.body 

            )}

        </div>

    );

};

export default ContextMenuButton;